class GiftWithPurchase extends HTMLElement {
  selectors = {
    message: {
      unreached: "gift-with-purchase-notification[type='unreached']",
      reached: "gift-with-purchase-notification[type='reached']",
      giftAdded: "gift-with-purchase-notification[type='gift-added']"
    }
  };

  attributes = {
    cart: {
      threshold: "cart-threshold",
      total: "cart-total"
    },
    thresholdReached: "threshold-reached",
    giftAdded: "gift-added"
  };

  modifiers = {
    hidden: "hide"
  };

  get threshold() {
    return parseFloat(this.getAttribute(this.attributes.cart.threshold));
  }

  set threshold(value) {
    this.setAttribute(this.attributes.cart.threshold, value);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute(this.attributes.cart.total));
  }

  set totalPrice(value) {
    this.setAttribute(this.attributes.cart.total, value);
  }

  get isThresholdReached() {
    return this.totalPrice >= this.threshold;
  }

  set isThresholdReached(value) {
    this.setAttribute(this.attributes.thresholdReached, value);
  }

  get isGiftAdded() {
    return this.getAttribute(this.attributes.giftAdded) === "true";
  }

  set isGiftAdded(value) {
    this.setAttribute(this.attributes.giftAdded, value);
  }

  constructor() {
    super();

    this.onCartChangedListener = this.onCartChange.bind(this);
  }

  connectedCallback() {
    this.threshold = this.getAttribute(this.attributes.cart.threshold);

    this.messageUnreachedElement = document.querySelector(this.selectors.message.unreached);
    this.messageReachedElement = document.querySelector(this.selectors.message.reached);
    this.messageGiftAddedElement = document.querySelector(this.selectors.message.giftAdded);

    document.addEventListener("theme:cart:change", this.onCartChangedListener);
  }

  disconnectedCallback() {
    document.removeEventListener("theme:cart:change", this.onCartChangedListener);
  }

  onCartChange(event) {
    const items = (event?.detail?.cart ?? event?.detail)?.items;

    if (!items?.length) {
      return false;
    }

    this.totalPrice = items
      .reduce((sum, item) => sum + item.original_line_price, 0);

    this.isThresholdReached = this.totalPrice >= this.threshold;

    // Check if any line item has a discount amount equal to the original line price, indicating the item is free.
    // If true, consider the gift as already added to the cart.
    this.isGiftAdded = items
      .some((item) => item.discounts.some(discount => discount.amount === item.original_price));

    this.updateMessages();
    this.updateWidget();
  }

  updateMessages() {
    if (this.isThresholdReached) {
      if (this.isGiftAdded) {
        this.messageReachedElement?.classList.add(this.modifiers.hidden);
        this.messageUnreachedElement?.classList.add(this.modifiers.hidden);
        this.messageGiftAddedElement?.classList.remove(this.modifiers.hidden);
      } else {
        this.messageReachedElement?.classList.remove(this.modifiers.hidden);
        this.messageUnreachedElement?.classList.add(this.modifiers.hidden);
        this.messageGiftAddedElement?.classList.add(this.modifiers.hidden);
      }
    } else {
      this.messageReachedElement?.classList.add(this.modifiers.hidden);
      this.messageUnreachedElement?.classList.remove(this.modifiers.hidden);
      this.messageGiftAddedElement?.classList.add(this.modifiers.hidden);
    }
  }

  updateWidget() {
    const shouldShowWidget = this.isThresholdReached && !this.isGiftAdded;

    this.classList.toggle(this.modifiers.hidden, !shouldShowWidget);
  }
}

if (!customElements.get("gift-with-purchase")) {
  customElements.define("gift-with-purchase", GiftWithPurchase);
}

class GiftWithPurchaseProduct extends HTMLElement {
  attributes = {
    productHandle: "product-handle"
  };

  modifiers = {
    hidden: "hide",
    loading: "loading"
  };

  constructor() {
    super();
  }

  async connectedCallback() {
    await this.setContent(this.productHandle);

    document.addEventListener("product-quick-buy:stitched-product-selected", async (event) => {
      event.stopPropagation();

      const productHandle = event.detail.productHandle;

      this.classList.add(this.modifiers.loading);

      await this.setContent(productHandle);

      this.classList.remove(this.modifiers.loading);
    });
  }

  get productHandle() {
    return this.getAttribute(this.attributes.productHandle);
  }

  async setContent(productHandle) {
    if (!productHandle) {
      return;
    }

    const productQuickBuyHTML = await this.fetchQuickBuyContent(productHandle);

    if (!productQuickBuyHTML) {
      this.classList.add(this.modifiers.hide);
    }

    this.innerHTML = productQuickBuyHTML;
  }

  async fetchQuickBuyContent(productHandle) {
    if (!productHandle) {
      return;
    }

    const url = `${window.Shopify.routes.root}products/${productHandle}?view=gift-with-purchase-quick-buy-html`;

    return await fetch(url).then(
      (data) => data.text()
    );
  }
}

if (!customElements.get("gift-with-purchase-product")) {
  customElements.define("gift-with-purchase-product", GiftWithPurchaseProduct);
}

class GiftWithPurchaseProductForm extends HTMLElement {
  selectors = {
    drawer: "gift-with-purchase-drawer",
    form: {
      container: "form",
      submit: {
        container: "[data-add-to-cart]",
        text: "[data-add-to-cart-text]"
      }
    },
    stitchedProduct: "[data-stitched-product-id]",
    stitchedProductsJson: "[data-stitched-products-json]"
  };

  attributes = {
    productHandle: "product-handle",
    enableProductStitching: "enable-product-stitching",
    stitchedProductHandle: "data-stitched-product-handle"
  };

  datasets = {
    option: "option"
  };

  modifiers = {
    hidden: "hide",
    disabled: "disabled"
  };

  colorOptionLabels = "color,colour,couleur,colore,farbe,색,色,fxärg,farve".split(",");
  sizeOptionLabels = "size,taille,größe,tamanho,tamaño,koko,サイズ".split(",");

  constructor() {
    super();
  }

  get productHandle() {
    return this.getAttribute(this.attributes.productHandle);
  }

  get enableProductStitching() {
    return this.getAttribute(this.attributes.enableProductStitching) === "true";
  }

  async connectedCallback() {
    this.product = await this.initProduct();

    this.initElements();

    this.initProductForm();

    this.stitchedProducts = this.initStitchedProducts();

    this.initStitchedProductSelection();

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();
  }

  async initProduct() {
    const url = `${window.Shopify.routes.root}products/${this.productHandle}.js`;

    const response = await fetch(url).then(
      (data) => data.text()
    );

    return JSON.parse(response);
  }

  initElements() {
    this.productFormElement = this.querySelector(this.selectors.form.container);

    this.productFormSubmitElement = this.querySelector(this.selectors.form.submit.container);
    this.productFormSubmitTextElement = this.querySelector(this.selectors.form.submit.text);

    this.giftWithPurchaseDrawer = this.closest(this.selectors.drawer);
  }

  initStitchedProducts() {
    if (!this.enableProductStitching) {
      return {};
    }

    const stitchedProductsJson = this.querySelector(this.selectors.stitchedProductsJson);

    if (!stitchedProductsJson) {
      return {};
    }

    return JSON.parse(stitchedProductsJson.innerHTML);
  }

  initProductForm() {
    this.productForm = new Shopify.theme.productForm.ProductForm(
      this.productFormElement,
      this.product,
      {
        onOptionChange: this.onOptionChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this),
        onQuantityChange: () => {},
        onPropertyChange: () => {}
      }
    );

    this.productOptionInputs = this.productForm.optionInputs.reduce(
      (options, input) => {
        const option = input.dataset[this.datasets.option];
        const inputs = options[option] || [];

        return {
          ...options,
          [option]: [...inputs, input]
        };
      },
      {}
    );

    this.currentVariant = this.productForm.variant();
  }

  onOptionChange(event) {
    this.selectVariant(event.dataset.variant);

    this.updateProductFormSubmit();

    this.disableUnavailableOptions();
    this.disableUnavailableStitchedProducts();
  }

  async onFormSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const formDataJson = this.formDataToJSON(formData);

    const url = `${window.theme.routes.cart}/add.js`;

    try {
      await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          items: [{ ...formDataJson }]
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      await this.updateCart();

      setTimeout(() => {
        this.giftWithPurchaseDrawer.dispatchEvent(new CustomEvent("gift-with-purchase:add"));
      }, 250);
    } catch (error) {
      console.error(error);
    }
  }

  selectVariant(variant) {
    const previousVariant = this.currentVariant;

    this.currentVariant = variant;

    this.productFormElement.dispatchEvent(new CustomEvent("variant:change", {
      bubbles: true,
      detail: {
        product: this.product,
        variant: this.currentVariant,
        previousVariant
      }
    }));
  }

  updateProductFormSubmit() {
    if (!this.productFormSubmitElement || !this.productFormSubmitTextElement) {
      return;
    }

    if (!this.currentVariant) {
      this.productFormSubmitElement.disabled = true;
      this.productFormSubmitTextElement.innerText = theme.strings.product.unavailable;

      return;
    }

    if (!this.currentVariant.available) {
      this.productFormSubmitElement.disabled = true;
      this.productFormSubmitTextElement.innerText = theme.strings.product.sold_out;
    } else {
      this.productFormSubmitElement.disabled = false;
      this.productFormSubmitTextElement.innerText = theme.strings.product.add_gift_to_cart;
    }
  }

  initStitchedProductSelection() {
    if (!Object.keys(this.stitchedProducts).length) {
      return;
    }

    this.customDelegate(
      "click",
      this.selectors.stitchedProduct,
      this.onStitchedProductSelect.bind(this)
    );
  }

  onStitchedProductSelect(event) {
    event.preventDefault();

    const stitchedProductSwatch = event.target.closest(this.selectors.stitchedProduct);
    const stitchedProductHandle = stitchedProductSwatch.getAttribute(this.attributes.stitchedProductHandle);

    document.dispatchEvent(new CustomEvent("product-quick-buy:stitched-product-selected", {
      detail: {
        productHandle: stitchedProductHandle
      }
    }));
  }

  validateOptionSelection() {
    return Object.values(this.productOptionInputs).every(inputs => inputs.some(input => input.checked));
  }

  disableUnavailableOptions() {
    if (!Object.keys(this.productOptionInputs).length) {
      return;
    }

    const areAllOptionsSelected = this.validateOptionSelection();

    if (!areAllOptionsSelected) {
      return;
    }

    if (!this.currentVariant) {
      return;
    }

    const selectedOptions = this.getSelectedOptions(this.currentVariant);

    const availableOptions = this.getAvailableValues(
      this.product.variants,
      selectedOptions
    );

    Object.keys(this.productOptionInputs).forEach((option) => {
      const availableValues = availableOptions[option] ?? [];

      this.productOptionInputs[option].forEach((input) => {
        if (!availableValues.includes(input.value)) {
          input.classList.add(this.modifiers.disabled);
          return;
        }

        input.classList.remove(this.modifiers.disabled);
      });
    });
  }

  disableUnavailableStitchedProducts() {
    if (!Object.keys(this.productOptionInputs).length || !Object.keys(this.stitchedProducts).length) {
      return;
    }

    const areAllOptionsSelected = this.validateOptionSelection();

    if (!areAllOptionsSelected) {
      return;
    }

    Object.values(this.stitchedProducts).forEach((stitchedProduct) => {
      const stitchedProductId = stitchedProduct.id;

      const stitchedProductSwatch = this.querySelector(`[data-stitched-product-id='${stitchedProductId}']`);

      if (!stitchedProductSwatch) {
        return;
      }

      const stitchedProductVariant = stitchedProduct.current_variant;
      const stitchedProductOptions = stitchedProduct.options;

      const colorOption = stitchedProductOptions.find((option) => {
        const downcasedOptionName = option.name.toLowerCase();
        return this.colorOptionLabels.includes(downcasedOptionName);
      });

      if (!colorOption) {
        return false;
      }

      const colorOptionPosition = `option${colorOption.position}`;
      const colorOptionValue = stitchedProductVariant[colorOptionPosition];

      let selectedOptions = {
        ...this.getSelectedOptions(),
        [colorOptionPosition]: colorOptionValue
      };

      const stitchedProductAvailableOptions = this.getAvailableValues(stitchedProduct.variants, selectedOptions);

      stitchedProductOptions.forEach((stitchedProductOption) => {
        const optionPosition = `option${stitchedProductOption.position}`;

        if (optionPosition === colorOptionPosition) {
          return;
        }

        const availableValues = stitchedProductAvailableOptions[optionPosition] ?? [];
        const currentVariantOptionValue = this.currentVariant[optionPosition];

        if (!currentVariantOptionValue) {
          return;
        }

        if (!availableValues.includes(currentVariantOptionValue)) {
          stitchedProductSwatch.classList.add(this.modifiers.disabled);
          return;
        }

        stitchedProductSwatch.classList.remove(this.modifiers.disabled);
      });
    });
  }

  getSelectedOptions() {
    return this.productForm.options().reduce((options, option, index) => {
      return {
        ...options,
        [`option${index + 1}`]: option.value
      };
    }, {});
  }

  getAvailableValues(variants, selected) {
    let originalSelectedKeys = Object.keys(selected);
    let maxOptionKey = `option${originalSelectedKeys.length + 1}`;
    let variantOptions = variants.map((variant) => {
      let keys = Object.keys(variant).filter((key) => {
        return key.indexOf("option") === 0 || key === "available";
      });

      return Object.assign(
        {},
        {},
        ...keys.map((key) => {
          if (key === "available") {
            return {
              [key]: variant[key]
            };
          }

          if (key > maxOptionKey) {
            return {};
          }

          const value = variant[key];

          if (value) {
            return {
              [key]: value
            };
          }

          return {};
        })
      );
    });

    Object.keys(variantOptions[0]).forEach((key) => {
      if (key === "available") {
        return;
      }

      if (selected.hasOwnProperty(key)) {
        return;
      }

      selected[key] = null;
    });

    let selectedKeys = Object.keys(selected);
    let combinations =
      selectedKeys.length > 0
        ? selectedKeys.map((excludeKey) => {
          return Object.assign(
            {},
            {},
            ...selectedKeys.map((key) => {
              if (excludeKey === key) {
                return {};
              }

              return {
                [key]: selected[key]
              };
            })
          );
        })
        : [{}];

    let availableValues = {};
    variantOptions
      .map((options) => {
        return Object.assign(
          {},
          {},
          ...combinations.map((combination) => {
            const isSubset =
              Object.keys(combination)
                .map((key) => {
                  if (combination[key] === null) {
                    return true;
                  }
                  return (
                    options.hasOwnProperty(key) &&
                    combination[key] === options[key]
                  );
                })
                .includes(false) !== true;

            if (!isSubset) {
              return {};
            }

            if (!options.available) {
              return {};
            }

            return Object.assign(
              {},
              {},
              ...Object.keys(options)
                .filter((key) => {
                  return (
                    key !== "available" &&
                    !combination.hasOwnProperty(key)
                  );
                })
                .map((optionName) => {
                  return {
                    [optionName]: [options[optionName]]
                  };
                })
            );
          })
        );
      })
      .filter((options) => {
        return Object.keys(options).length > 0;
      })
      .forEach((options) => {
        Object.keys(options).forEach((key) => {
          if (!availableValues.hasOwnProperty(key)) {
            availableValues[key] = [];
          }
          availableValues[key] = availableValues[key]
            .concat(options[key])
            .filter((value, index, self) => {
              return self.indexOf(value) === index;
            });
        });
      });

    return Object.assign(
      {},
      {},
      ...Object.keys(availableValues)
        .sort()
        .map((key) => {
          return {
            [key]: availableValues[key]
          };
        })
    );
  }

  formDataToJSON(formData) {
    return Array.from(formData.entries()).reduce((memo, [key, value]) => ({
      ...memo,
      [key]: value,
    }), {});
  }

  async updateCart() {
    const url = `${Shopify.routes.root}cart.js`;

    const response = await fetch(url).then(
      (data) => data.json()
    );

    document.dispatchEvent(new CustomEvent("theme:cart:reload", {
      detail: {
        cart: response
      },
      bubbles: true
    }));

    document.dispatchEvent(new CustomEvent("theme:cart:change", {
      detail: {
        cart: response
      },
      bubbles: true
    }));
  }
}

if (!customElements.get("gift-with-purchase-product-form")) {
  customElements.define("gift-with-purchase-product-form", GiftWithPurchaseProductForm);
}
